import type { User } from "../types";

interface JwtPayload {
  exp?: number;
  token_use?: "access" | "bootstrap" | "session_management";
  session_cap?: number;
  issuer?: {
    id?: string | number;
    username?: string;
    role?: number | string;
    companyId?: number | string;
    displayName?: string;
    sessionId?: string;
    type?: string;
  };
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  return (
    payload == null || (payload.exp != null && payload.exp * 1000 < Date.now())
  );
}

export function authIdentity(token: string | null): string | null {
  const issuer = token ? decodeJwt(token)?.issuer : null;
  if (!issuer?.id || !issuer.companyId || !issuer.sessionId) return null;
  return JSON.stringify([issuer.type, String(issuer.id), String(issuer.companyId), issuer.sessionId]);
}

/**
 * Derive the app user shape from the JWT `issuer` claim.
 * Returns null when the token cannot be decoded or has no identity.
 */
export function extractUserFromToken(token: string): User | null {
  const payload = decodeJwt(token);
  if (!payload) return null;

  const issuer = payload.issuer;
  if (typeof issuer !== "object" || issuer === null) return null;

  const id = issuer.id ?? issuer.username;
  if (id == null) return null;

  const rawRole = issuer.role;
  const role =
    typeof rawRole === "number"
      ? rawRole
      : typeof rawRole === "string"
        ? Number(rawRole)
        : 0;

  return {
    userId: String(id),
    companyId: String(issuer.companyId ?? ""),
    role: Number.isFinite(role) ? role : 0,
    displayName: String(issuer.displayName ?? issuer.username ?? ""),
    email: String(issuer.username ?? ""),
  };
}
